import {
  type Dispatch,
  forwardRef,
  type SetStateAction,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import useMeasure, { type RectReadOnly } from 'react-use-measure';
import { motion } from 'framer-motion';
import mergeRefs from 'merge-refs';

import { useCountDownTimer } from '@/common/hooks/count-down-timer';
import { timeStringFormat } from '@/common/lib/timer';
import { H2, TextXSmall } from '@/components/Typography/Typography';

interface ProductionScaleProps {
  activeIsTarget: boolean;
  toAdd: number;
  limit?: number;
  localStock: number;
  incommingStorage: number;
  productionTimeLeft?: number;
  setPullCoords?: Dispatch<SetStateAction<RectReadOnly | null>>;
}

const ProductionScale = forwardRef<HTMLDivElement, ProductionScaleProps>(
  (
    {
      activeIsTarget,
      toAdd,
      limit,
      localStock,
      incommingStorage,
      productionTimeLeft,
      setPullCoords,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const [scaleRef, coords] = useMeasure();
    const secondsToEndProduction = useCountDownTimer(productionTimeLeft || 0);

    const darkBlueWidth = limit
      ? ((localStock + incommingStorage) * 100) / limit
      : 0;
    const blueWidth = limit ? (toAdd * 100) / limit : 0;

    useEffect(() => {
      setPullCoords?.(coords);
    }, [coords]);

    return (
      <div ref={mergeRefs(ref, (ref) => scaleRef(ref))} className="flex-1">
        {activeIsTarget ? (
          <div className="relative flex-1 h-12">
            <div className="relative overflow-hidden h-full w-full bg-white/10 backdrop-blur-3xl rounded-[80px] flex">
              <div className="absolute z-[1] top-0 left-0 w-full h-full flex items-center justify-center px-3">
                <H2 weight="bold">
                  {timeStringFormat(secondsToEndProduction * 1000)}
                </H2>
              </div>
              <motion.span
                transition={{ duration: 0.4, ease: 'linear' }}
                animate={{ width: `${darkBlueWidth}%` }}
                initial={{ width: `${darkBlueWidth}%` }}
                className="h-full bg-[#2C5897] relative"
              ></motion.span>
              <motion.span
                transition={{ duration: 0.4, ease: 'linear' }}
                initial={{ width: `${blueWidth + darkBlueWidth}%` }}
                animate={{ width: `${blueWidth + darkBlueWidth}%` }}
                className="h-full bg-blue absolute -z-10 left-0 top-0"
              ></motion.span>
            </div>
          </div>
        ) : (
          <TextXSmall
            weight="regular"
            className="!text-white/60 text-center !leading-1.2"
          >
            {t(
              'To add another item to the production, wait for the production to complete or clear the queue.',
            )}
          </TextXSmall>
        )}
      </div>
    );
  },
);

export { ProductionScale };
